import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { GlassCard } from "@/components/GlassCard";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { container, goldBtn, inputCls, textareaCls, labelCls, ORG_TYPES, AUM } from "@/lib/constants";

function Field({ label, children }) {
  return (<div><label className={labelCls}>{label}</label>{children}</div>);
}

export default function Auth() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login, refresh } = useAuth();
  const [tab, setTab] = useState(params.get("tab") === "apply" ? "apply" : "signin");
  const [busy, setBusy] = useState(false);

  const [signin, setSignin] = useState({ email: "", password: "" });
  const [app, setApp] = useState({
    full_name: "", organisation_name: "", organisation_type: "family_office", role_title: "",
    email: "", phone: "", country_of_incorporation: "", aum_range: "250m_1bn",
    mandate_statement: "", password: "",
  });
  const setA = (k) => (e) => setApp({ ...app, [k]: e.target.value });

  const doSignIn = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await login(signin.email, signin.password);
      toast.success("Welcome back.");
      navigate(res.redirect || "/deals");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password.");
    } finally { setBusy(false); }
  };

  const doApply = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/apply", app);
      // auto sign-in so the applicant has a session on the holding page
      try { await login(app.email, app.password); await refresh(); } catch (_) {}
      toast.success("Application received. Please confirm your email.");
      navigate("/access-pending");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Application could not be submitted.");
    } finally { setBusy(false); }
  };

  const tabBtn = (key, label, testid) => (
    <button
      type="button"
      data-testid={testid}
      onClick={() => setTab(key)}
      className={`flex-1 h-11 rounded-lg text-sm transition-colors duration-200 ${
        tab === key ? "bg-[hsl(var(--pdg-gold)/0.14)] text-[hsl(var(--pdg-gold))] border border-[hsl(var(--pdg-gold)/0.4)]"
          : "text-foreground/70 hover:text-foreground border border-transparent"
      }`}
    >{label}</button>
  );

  return (
    <PublicLayout>
      <SEO title={tab === "apply" ? "Apply for Access" : "Sign In"} description="Sign in or apply for access to Prime Deal Gateway." noindex />
      <section className={`${container} py-16 lg:py-24`}>
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-8">
            <div className="pdg-kicker">By invitation</div>
            <h1 className="mt-3 pdg-serif text-4xl text-foreground">{tab === "apply" ? "Apply for Access" : "Sign In"}</h1>
          </div>
          <GlassCard className="p-6 sm:p-8">
            <div data-testid="auth-tabs" className="flex gap-2 p-1 rounded-xl bg-[hsl(var(--pdg-elevated)/0.5)] mb-7">
              {tabBtn("signin", "Sign In", "auth-signin-tab")}
              {tabBtn("apply", "Apply for Access", "auth-apply-tab")}
            </div>

            {tab === "signin" ? (
              <form onSubmit={doSignIn} className="space-y-5">
                <Field label="Email">
                  <input type="email" required data-testid="signin-email-input" className={inputCls}
                    value={signin.email} onChange={(e) => setSignin({ ...signin, email: e.target.value })} />
                </Field>
                <Field label="Password">
                  <input type="password" required data-testid="signin-password-input" className={inputCls}
                    value={signin.password} onChange={(e) => setSignin({ ...signin, password: e.target.value })} />
                </Field>
                <button disabled={busy} data-testid="auth-signin-submit-button" className={goldBtn + " w-full"}>
                  {busy ? "Signing in…" : "Sign In"}
                </button>
                <p className="text-xs text-muted-foreground text-center">
                  Not yet a member? <button type="button" onClick={() => setTab("apply")} className="text-[hsl(var(--pdg-gold))]">Apply for access</button>
                </p>
              </form>
            ) : (
              <form onSubmit={doApply} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full name"><input required data-testid="apply-full-name-input" className={inputCls} value={app.full_name} onChange={setA("full_name")} /></Field>
                  <Field label="Organisation name"><input required data-testid="apply-org-name-input" className={inputCls} value={app.organisation_name} onChange={setA("organisation_name")} /></Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Organisation type">
                    <select data-testid="apply-org-type-select" className={inputCls} value={app.organisation_type} onChange={setA("organisation_type")}>
                      {Object.entries(ORG_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </Field>
                  <Field label="Role"><input required data-testid="apply-role-input" className={inputCls} value={app.role_title} onChange={setA("role_title")} /></Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Email"><input type="email" required data-testid="apply-email-input" className={inputCls} value={app.email} onChange={setA("email")} /></Field>
                  <Field label="Phone"><input required data-testid="apply-phone-input" className={inputCls} value={app.phone} onChange={setA("phone")} /></Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Country of incorporation"><input required data-testid="apply-country-input" className={inputCls} value={app.country_of_incorporation} onChange={setA("country_of_incorporation")} /></Field>
                  <Field label="AUM range">
                    <select data-testid="apply-aum-select" className={inputCls} value={app.aum_range} onChange={setA("aum_range")}>
                      {Object.entries(AUM).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Statement of investment mandate">
                  <textarea required rows={4} data-testid="apply-mandate-input" className={textareaCls} value={app.mandate_statement} onChange={setA("mandate_statement")} placeholder="Sectors, geographies, ticket size, and objectives." />
                </Field>
                <Field label="Create a password">
                  <input type="password" required minLength={8} data-testid="apply-password-input" className={inputCls} value={app.password} onChange={setA("password")} />
                </Field>
                <button disabled={busy} data-testid="auth-apply-submit-button" className={goldBtn + " w-full"}>
                  {busy ? "Submitting…" : "Submit application"}
                </button>
                <p className="text-xs text-muted-foreground text-center leading-6">
                  By applying you agree to our <Link to="/legal/terms" className="text-[hsl(var(--pdg-gold))]">Terms</Link> and <Link to="/legal/privacy" className="text-[hsl(var(--pdg-gold))]">Privacy Policy</Link>.
                </p>
              </form>
            )}
          </GlassCard>
        </div>
      </section>
    </PublicLayout>
  );
}
